/**
 * The manifest opt-in every `/host/refresh` caller must send (ENG-9585).
 *
 * WHY THIS IS A SHARED CONSTANT RATHER THAN THREE LITERALS PER CALL SITE.
 *
 * `/host/refresh` answers in one of two shapes. Send these flags and knowledge,
 * root skills and integration skills arrive as manifests — slug + content hash
 * — which the host resolves from its own cache. Send nothing and the API
 * answers with every BODY inline, which is what it did before the manifests
 * existed. The server has no way to tell a caller that forgot the flags from
 * one that genuinely wants bodies, so an omission is not an error anywhere: the
 * request succeeds, the response is correct, and it is simply ~9x larger.
 *
 * That is exactly what happened. Two of the four callers in `manager-worker.ts`
 * — the restart lane and the fast-MCP respawn — posted `{ agent_id }` alone and
 * kept taking the legacy shape long after the poll beside them had moved. It
 * was invisible from both ends: no error, no warning, nothing in `manager.log`,
 * and on the server a perfectly ordinary 200.
 *
 * MEASURED ON PROD, 2026-08-30, over the 8h after fleet uptake plateaued:
 * legacy-shaped polls were 12.6% of requests and 38.7% of ALL `/host/refresh`
 * egress — mean 290 KB against the manifest shape's 66 KB, and 2.96 MB against
 * 325 KB on the largest single agent measured. Roughly two fifths of what the
 * endpoint still costs came from one in eight requests that had simply been
 * left behind.
 *
 * Centralising the flags does not make the saving; adding them to those two
 * call sites does. It makes the NEXT call site correct by default, which is the
 * half that failed here — both offenders were written as `{ agent_id }`, the
 * obvious thing to type, at a time when there was nothing to suggest otherwise.
 *
 * WHAT A CALLER TAKES ON BY SENDING THESE. The response carries hashes, not
 * bodies. A caller that needs a body must resolve it (the poll does, via
 * `resolveKnowledgeFromManifest` / the skill-cache resolvers) — it CANNOT read
 * `refreshData.knowledge` and find content, because that key will not be there.
 * Both lanes this was added for are safe by inspection: everything downstream of
 * them reads only `agent`, `team`, `channel_configs`, `tools`, `openrouter`,
 * `anthropic` and `model_defaults`, and none of those move between the two
 * shapes. See `eng-9585-refresh-manifest-flags.test.ts`, which pins that.
 *
 * FORWARD AND BACKWARD COMPATIBLE. An API build predating a flag ignores it and
 * answers with bodies, which is the pre-manifest behaviour and still correct —
 * so a manager may always send all three regardless of what it is talking to.
 * They stay three separate flags rather than one `manifests: true`, because
 * they shipped in different CLI versions and an API that understands one but
 * not another must still be able to answer; folding them together would make
 * the older capability unreachable mid-rollout.
 */
export const REFRESH_MANIFEST_FLAGS = {
  /** Knowledge as slug + hash instead of inline bodies. */
  knowledge_manifest: true,
  /** Global + shared ROOT skills as metadata + hash. */
  skills_manifest: true,
  /**
   * Integration CHILD skills as metadata + hash. Deliberately not folded into
   * `skills_manifest`: a manager that claimed the family but could not decode
   * this lane would read it as empty, and an empty integration-skill list is
   * what the orphan sweep treats as "every integration was uninstalled" — it
   * deletes the folders off disk. The flag has to name the exact capability.
   */
  integration_skills_manifest: true,
} as const;
